<template>
  <section class="main">
    <h1 class="intro">{{ $t("MY_DYLIBS") }}</h1>
    <loader v-if="loading" />
    <div v-else class="row">
      <div class="row__col_grow">
        <html-with-links
          tag="p"
          class="g-text"
          :html="$t('MY_DYLIBS_DESC', {configureLink: '/my/configure'})"
        />

        <div class="items">
          <div v-for="dylib in dylibs" class="item item_active" :key="dylib">
            <div class="item__desc">
              <span class="item__title">
                {{ dylib }}
                <a
                  href="#"
                  class="item__remove"
                  ref="removeDylib"
                  @click.prevent="removeDylib(dylib)">
                  {{
                    dylibsBeingRemoved.includes(dylib)
                      ? $t("APP_REMOVING")
                      : $t("REMOVE")
                  }}
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="upload">
        <form class="upload__box">
          <input
            @change="newFilesHandler"
            class="upload__input"
            ref="upload"
            type="file"
            multiple
          />
          <v-button
            type="primary"
            @click.native.prevent="select"
            :loading="uploadingFiles"
            class="upload__button"
          >
            {{ $t("UPLOAD_FILES") }}
          </v-button>
          <v-button
            @click.native.prevent="uploadByUrl"
            :loading="uploadingByUrl"
            type="primary"
            class="upload__button"
          >{{ $t("UPLOAD_BY_URL") }}</v-button>
        </form>
      </div>
    </div>
  </section>
</template>

<script>
import HtmlWithLinks from '@/components/HtmlWithLinks';
import VButton from '@/components/Button';
import Loader from '@/components/Loader';

export default {
  components: { HtmlWithLinks, VButton, Loader },
  data() {
    return {
      loading: true,
      dylibs: [],
      uploads: [],
      uploadingFiles: false,
      uploadingByUrl: false,
      dylibsBeingRemoved: [],
    };
  },
  computed: {
    title() {
      return this.$t('MY_DYLIBS');
    },
  },
  methods: {
    select() {
      this.$refs.upload.click();
    },
    getDylibs() {
      return this.$api.getDylibs().then((response) => {
        this.dylibs = response;
      }).finally(() => {
        this.loading = false;
      });
    },
    removeDylib(name) {
      if (this.dylibsBeingRemoved.includes(name)) {
        return;
      }

      this.dylibsBeingRemoved.push(name);

      this.$api.deleteDylib(name)
        .then(() => {
          const idx = this.dylibs.indexOf(name);
          this.dylibs.splice(idx, 1);
        })
        .finally(() => {
          this.dylibsBeingRemoved = this.dylibsBeingRemoved.filter(n => n !== name);
        });
    },
    newFilesHandler() {
      this.uploadingFiles = true;

      const uploads = [...this.$refs.upload.files].map(file => this.$api.addDylib({ file }));

      Promise.allSettled(uploads)
        .then(() => this.getDylibs())
        .finally(() => {
          this.uploadingFiles = false;
          this.$refs.upload.value = null;
        });
    },
    uploadByUrl() {
      this.$prompt(this.$t('PROVIDE_URL_TO_DYLIB'))
        .then((url) => {
          this.uploadingByUrl = true;
          return this.$api.addDylib({ url });
        })
        .then(() => this.getDylibs())
        .finally(() => {
          this.uploadingByUrl = false;
        });
    },
  },
  mounted() {
    this.getDylibs();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;

  &__app {
    padding: 10px 0;
    border-bottom: 1px solid #e5e5e5;
  }
}

.email {
  display: block;
  font-weight: 600;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e5e5;
  margin-top: 20px;

  &__title {
    color: var(--text-primary);
    font-weight: 600;
    font-size: 16px;
  }

  &__storage {
    background: var(--lighter-primary);
    border-radius: 2px;
    padding: 3px;
  }
}

.row {
  display: flex;

  @include notebook {
    flex-direction: column-reverse;
  }

  &__col {
    &_grow {
      flex-grow: 1;
    }
  }
}

.upload {
  flex: 0 0 200px;
  margin-left: 40px;

  @include notebook {
    margin: 0 auto 20px;
    flex: 0 0 auto;
  }

  &__input {
    opacity: 0;
    position: absolute;
    z-index: -1;
  }

  &__box {
    background: var(--bg);
    border: 1px solid #ebebeb;
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);
    border-radius: 8px;
    padding: 15px;
  }

  &__button {
    display: block;
    background: $primary;
    border-radius: 2px;
    color: #fff;
    font-size: 14px;
    padding: 7px;
    width: 100%;

    & + .upload__button {
      margin-top: 10px;
    }
  }

  &__num {
    display: block;
    text-align: center;
    font-size: 12px;
    margin: 10px 0;
  }

  &__item {
    border-top: 1px solid #e5e5e5;
    padding: 10px 0;

    &:last-child {
      padding-bottom: 0;
    }
  }

  &__title {
    font-size: 12px;
    font-weight: 500;
    word-break: break-word;
  }

  &__progress-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: $text-secondary;
    font-size: 12px;
    margin-top: 4px;
  }

  &__progress {
    flex: 0 0 130px;
    height: 3px;
    position: relative;
    background: #d8d8d8;
    border-radius: 2px;

    &-inner {
      height: 3px;
      position: absolute;
      background: $primary;
      border-radius: 2px;
      width: 0;
    }
  }

  &__status {
    display: block;
    word-break: break-word;
    background: var(--bg-success);
    border-radius: 2px;
    color: #3c4f3e;
    font-size: 12px;
    padding: 3px;
    margin: 6px 0;

    &_error {
      background: var(--bg-error);
      color: var(--configure-error-text);
    }
  }

  &__date {
    display: flex;
    justify-content: space-between;
    color: $text-secondary;
    font-size: 10px;
  }

  &__delete {
    margin-left: 5px;
    cursor: pointer;
  }
}

.help {
  font-size: 12px;
  margin: 10px 10px 0;
  padding-top: 10px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  align-items: flex-start;

  &__icon {
    margin-top: 3px;
    margin-right: 5px;
  }
}

.items {
  margin-top: 20px;
  flex-grow: 1;
}

.item {
  display: flex;
  padding: 10px;
  align-items: center;
  border-radius: 4px;
  margin-bottom: 15px;
  @include transition;

  @include notebook {
    display: block;
  }

  &:not(:first-child) {
    margin-top: 5px;
  }

  &_active {
    box-shadow: var(--my-device-shadow);
    border-color: transparent;
  }

  &_disabled {
    pointer-events: none;
  }

  &__img-wrapper {
    flex: 0 0 44px;
    text-align: center;
    margin-right: 15px;
  }

  &__img {
    height: 45px;
    filter: var(--img-filter);
  }

  &__title {
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  &__pro {
    text-transform: uppercase;
    background: $primary;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    padding: 3px 3px 2px;
    margin-left: 5px;
  }

  &__remove {
    color: #fa6565;
    margin-left: auto;
    padding-left: 5px;
  }

  &__desc {
    flex-grow: 1;
  }

  &__info {
    line-height: 21px;
  }

  &__jail {
    white-space: nowrap;
    border-radius: 2.5px;
    font-size: 12px;
    padding: 1px 3px 2px;
    line-height: 10px;
    position: relative;
    top: -1px;
    margin-left: 2px;
    background: var(--tertiary-accent);
    color: var(--text-tertiary-accent);

    &_active {
      background: var(--bg-success);
      color: #053305;
    }
  }

  &__switch {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 10px;

    @include notebook {
      align-items: flex-start;
      margin-top: 5px;
    }
  }

  &__button {
    position: relative;
    background: var(--bg);
    border: 1px solid lighten($primary, 20%);
    border-radius: 2px;
    color: var(--text-primary);
    font-size: 12px;
    padding: 3px 15px;

    &_desktop {
      @include notebook {
        display: none;
      }
    }
  }

  &__switch-text {
    color: $text-secondary;
    font-size: 12px;
    margin-top: 2px;
  }
}
</style>
